import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { activeSubNavItemId } from "@/components/layout/moduleSubNavMatch";
import { ModuleSubNav } from "@/components/layout/ModuleSubNav";
import { PageSubNavSlotContext, type PageSubNavSlot } from "@/components/layout/pageSubNavSlot";
import { ModuleScope } from "@/components/ui/module-scope";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/usePermissions";
import { useUiUplift } from "@/hooks/useUiUplift";
import { CARE_PATH } from "@/lib/care-placement";
import { getModuleAccent } from "@/lib/module-accents";
import { peopleListUrl } from "@/lib/people-list-return";
import { peopleSubNavItems } from "./peopleSubNav";

const peopleAccent = getModuleAccent("people");

/**
 * Pages that take no People bar: L3 tasks own the screen (NAVIGATION_AND_STATES.md, T4/T6),
 * and the engagement dashboard belongs to no People section yet.
 */
const NO_BAR_PATHS = ["/community/merge", "/engagement"];

export interface PeopleSubNavHostProps {
  /**
   * The page. A function receives whether the bar is showing, for a caller (CommunityLayout)
   * that drops the legacy section menu in its place.
   */
  children: ReactNode | ((barShown: boolean) => ReactNode);
}

/**
 * Hosts People's module sub-nav (#6678) under ui_uplift = next. Wrap a People page in it: from
 * 640px the bar sits above the page. On a phone the selector goes under the title: the
 * component that draws the title claims it (see pageSubNavSlot), and if none does the bar
 * falls back to the top so the navigation is never lost. Renders nothing extra in the classic
 * layout, on a page that takes no bar, or when the viewer has fewer than two sections.
 */
export function PeopleSubNavHost({ children }: PeopleSubNavHostProps) {
  const { pathname, search } = useLocation();
  const { isOrgAdmin, isLoading: permissionsLoading, canAccessModule } = usePermissions();
  const isNext = useUiUplift() === "next";
  const isPhone = useIsMobile(640);
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((count) => count + 1);
    return () => setClaims((count) => count - 1);
  }, []);
  // Group management is an org-admin page (its route is behind OrgAdminRoute).
  const withGroups = isNext && isOrgAdmin && !permissionsLoading;
  const withCare = isNext;
  // While access loads, only Care (which needs no People access) is safe to assume on Care itself.
  const onCare = pathname === CARE_PATH || pathname.startsWith(`${CARE_PATH}/`);
  const withPeople = permissionsLoading ? !onCare : canAccessModule("people");
  const { items, moreItems } = useMemo(
    () => peopleSubNavItems({ withGroups, withCare, withPeople, withSettings: withGroups }),
    [withGroups, withCare, withPeople],
  );

  const onNoBarPage = NO_BAR_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  // ModuleSubNav itself renders nothing below two sections; skip its wrapper too.
  // Only where one of the viewer's sections owns the page: an admin with no People role has
  // just Groups and Care, so the directory or organizations would show a bar with nothing
  // current (and "People" as the phone label).
  const all = [...items, ...moreItems];
  // A person's record is shared by several lists (Everyone, Organizations, Visitors, …). It
  // belongs to the one it was opened from (`?from=`), when that list is one of the viewer's
  // sections; otherwise to Everyone, which owns `/people/`.
  const fromPath = peopleListUrl(new URLSearchParams(search).get("from"))?.split(/[?#]/)[0];
  const currentPath =
    fromPath && activeSubNavItemId(all, pathname) === "everyone" && activeSubNavItemId(all, fromPath) !== null
      ? fromPath
      : pathname;
  const ownsPage = activeSubNavItemId(all, currentPath) !== null;
  const barShown = isNext && !onNoBarPage && ownsPage && items.length + moreItems.length >= 2;

  // The bar carries People's accent itself. AppLayout classifies the page the same way, from the
  // same items (getModuleForRoute), so the page body agrees; this keeps the bar right on its own.
  const bar = useMemo(
    () =>
      barShown ? (
        <ModuleScope accent={peopleAccent}>
          <ModuleSubNav module="People" items={items} moreItems={moreItems} currentPath={currentPath} />
        </ModuleScope>
      ) : null,
    [barShown, items, moreItems, currentPath],
  );
  const slot = useMemo<PageSubNavSlot>(() => ({ node: bar, claim }), [bar, claim]);
  const content = typeof children === "function" ? children(barShown) : children;
  if (!barShown) return <>{content}</>;

  const onPhone = isPhone;
  return (
    <PageSubNavSlotContext.Provider value={onPhone ? slot : null}>
      {/* Above the page from 640px, or on a phone when no title component took the selector. */}
      {!onPhone || claims === 0 ? (
        <div className="mx-auto mb-[var(--layout-header-gap)] w-full max-w-[var(--layout-max-width)]">{bar}</div>
      ) : null}
      {content}
    </PageSubNavSlotContext.Provider>
  );
}
